import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import ResultBadge from "@/components/ResultBadge";
import { Brand } from "@/components/Brand";
import { ShieldStar, Translate, QrCode } from "@phosphor-icons/react";

export default function PublicVerify() {
    const { t, toggle, nextLangLabel } = useI18n();
    const [payload, setPayload] = useState("");
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    const verify = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/verify", { token: payload.trim() });
            setResult(data);
        } catch {
            setResult({ outcome: "red", reasons: ["network_error"] });
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-background">
            <header className="h-14 border-b border-border bg-card px-6 flex items-center justify-between">
                <Brand to="/" size="sm" dataTestId="public-brand" />
                <Button variant="ghost" size="sm" onClick={toggle} data-testid="public-lang-toggle"><Translate size={16} className="me-1" />{nextLangLabel()}</Button>
            </header>
            <div className="max-w-2xl mx-auto p-6 sm:p-10 space-y-6">
                <div>
                    <div className="text-[11px] tracking-[0.35em] uppercase text-primary">· {t("publicVerify")}</div>
                    <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">{t("scannerTitle")}</h1>
                    <p className="text-sm text-muted-foreground mt-1">{t("scannerHint")}</p>
                </div>
                <Card className="p-6 rounded-sm border-border">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-2"><QrCode size={14} className="inline me-1" />Paste signed payload from QR</div>
                    <Textarea rows={4} value={payload} onChange={(e) => setPayload(e.target.value)} className="mono text-xs" data-testid="public-payload" />
                    <Button onClick={verify} disabled={!payload.trim() || busy} className="mt-3 bg-primary hover:bg-primary/90 rounded-sm" data-testid="public-verify-btn">
                        {t("verify")}
                    </Button>
                </Card>
                {result && (
                    <div className="space-y-4">
                        <ResultBadge outcome={result.outcome} />
                        {result.reasons?.length > 0 && (
                            <Card className="p-4 rounded-sm border-border">
                                <div className="text-[10px] tracking-widest uppercase text-muted-foreground mb-2">Signals</div>
                                <ul className="text-sm mono space-y-1">{result.reasons.map((r) => <li key={r}>· {r}</li>)}</ul>
                            </Card>
                        )}
                        {result.plate && (
                            <Card className="p-4 rounded-sm border-border">
                                <div className="text-[10px] tracking-widest uppercase text-muted-foreground mb-2">Plate</div>
                                <div className="grid grid-cols-2 gap-2 text-sm mono">
                                    <div><b>Plate:</b> {result.plate.plate_number}</div>
                                    <div><b>Status:</b> {result.plate.status}</div>
                                    <div><b>Vehicle:</b> {result.plate.vehicle?.make} {result.plate.vehicle?.model}</div>
                                    <div><b>Year:</b> {result.plate.vehicle?.year}</div>
                                </div>
                            </Card>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
